import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { MenuItem, TextFieldProps } from '@mui/material';
import { CustomPhoneInput, PhoneNumberFieldContainer, PhoneSelect } from './styles';
import { countryOptions } from '@/helpers/countries';

const PhoneNumberField = forwardRef<HTMLInputElement, TextFieldProps & { onCountryChange: (code: string) => void }>(
  ({ error, helperText, onCountryChange, size, onChange, value, ...props }, ref) => {
    // Note: value and onChange are extracted above and handled via displayValue/handlePhoneNumberChange
    const [selectedCountry, setSelectedCountry] = useState('+1');
    const [invalidInput, setInvalidInput] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize displayValue with incoming value (numbers only)
    const [displayValue, setDisplayValue] = useState(() => {
      if (value && typeof value === 'string') {
        return value.replace(/\D/g, '');
      }
      return '';
    });

    // Update displayValue when external value changes (e.g., form reset)
    useEffect(() => {
      if (value !== undefined && typeof value === 'string') {
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly !== displayValue) {
          setDisplayValue(digitsOnly);
        }
      } else if (value === '' || value === null || value === undefined) {
        setDisplayValue('');
      }
    }, [value, displayValue]);

    const handleCountryChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      const newCountryCode = event.target.value as string;
      setSelectedCountry(newCountryCode);
      onCountryChange(newCountryCode);
    };

    // Format phone number as user types: XXX-XXX-XXXX for US numbers
    const formatPhoneNumber = (input: string): string => {
      // Remove all non-numeric characters
      const numbers = input.replace(/\D/g, '');

      // Limit to 10 digits for US numbers
      const trimmed = numbers.slice(0, 10);

      // Format as XXX-XXX-XXXX
      if (trimmed.length <= 3) {
        return trimmed;
      } else if (trimmed.length <= 6) {
        return `${trimmed.slice(0, 3)}-${trimmed.slice(3)}`;
      } else {
        return `${trimmed.slice(0, 3)}-${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
      }
    };

    const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target.value;

      // Check if input contains non-numeric characters
      if (input && /\D/.test(input)) {
        setInvalidInput(true);

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Clear the invalid input message after 2 seconds
        timeoutRef.current = setTimeout(() => {
          setInvalidInput(false);
          timeoutRef.current = null;
        }, 2000);
      }

      // Extract only numeric digits
      const digitsOnly = input.replace(/\D/g, '');
      const formatted = formatPhoneNumber(input);
      setDisplayValue(formatted);

      // Pass the digits-only value to the parent form
      if (onChange) {
        const syntheticEvent = {
          ...event,
          target: {
            ...event.target,
            value: digitsOnly,
          },
        };
        onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
      }
    };

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);
    const menuProps = {
      PaperProps: {
        style: {
          maxHeight: 260,
          marginTop: size === 'small' ? -40 : -56,
          borderRadius: '23px 0 0 23px',
        },
      },
    };

    return (
      <PhoneNumberFieldContainer>
        <PhoneSelect
          select
          value={selectedCountry}
          onChange={handleCountryChange}
          variant="outlined"
          error={error}
          size={size}
          slotProps={{
            select: {
              MenuProps: menuProps,
            },
          }}
        >
          {countryOptions.map((option) => (
            <MenuItem key={option.code} value={option.code} sx={{ fontSize: 24 }}>
              {option.label}
            </MenuItem>
          ))}
        </PhoneSelect>

        <CustomPhoneInput
          placeholder="Phone Number"
          variant="outlined"
          fullWidth
          error={error || invalidInput}
          size={size}
          helperText={invalidInput ? 'Please enter numbers only' : helperText}
          slotProps={{
            formHelperText: {
              sx: { marginLeft: -8 },
            },
          }}
          ref={ref}
          value={displayValue}
          onChange={handlePhoneNumberChange}
          {...props}
          inputProps={{ ...(props.inputProps ?? {}), autoComplete: 'off' }}
        />
      </PhoneNumberFieldContainer>
    );
  },
);

export default PhoneNumberField;
